/// <amd-module name="Widgets/QueryScalarWidget/ColorRuleInterpreter" />
/**
 * Used to verify that a string is a valid hex color code
 */
export class HexColorCodeValidator {
    private static hexColorRegex: RegExp = /^#(([0-9A-F]{6})|([0-9A-F]{3}))$/i; // Case-insensitive and matches 3 or 6 characters of 0-9 and/or A-F

    /**
     * Validates that the passed string is a valid hex color code that can be interpreted by a browser
     * @param {string} colorString - The string to test/verify
     * @returns true if the string is a valid hex color code
     */
    public static isValid(colorString: string): boolean {
        return HexColorCodeValidator.hexColorRegex.test(colorString);
    }
}

/** These rules describe non-default conditions for coloring the query tile.
 * This is evaluated like so:
 *   (actual queryResult) operator (thresholdCount)
 */
export interface IQueryColorRule {
    /** Determines if the rule is applicable. Notes on disabled state:
     *   -Editor and View do not treat malformed state of a disabled rule as a blocking issue.
     *   -Editor will still show last state, when disabled.
     */
    isEnabled: boolean;

    /** The comparison operator to use. Only recognized operators from our shortlist are allowed for use.*/
    operator: string;

    /** The right-side number is a user supplied value we use with the operator to evaluate the rule.*/
    thresholdCount: number;

    /** The color to apply, if the rule is relevant.
     * Well formed input is #RRGGBB(Clients should be tolerant of lower case). We don't need any other formats, as the content isn't directly user facing.*/
    backgroundColor: string;
}

/** The known set of query evaluation operators accepted for Coloring the Query Widget.*/
export let validQueryColorOperators: string[] = ["<", ">", "<=", ">=", "<>", "="];

/**
 * Used for verifying rules, testing if values satisfy those rules,
 * and interpreting which rules should be used to supply color values
 */
export class ColorRuleInterpreter {
    private static defaultBackgroundColor: string = "#009CCC";
    /**
     * Checks if the result count satisfies the color rule
     * @param {IQueryColorRule} colorRule - The rule to check against
     * @param {number} resultCount - The value to check against the rule
     * @returns true if the value satisfies the rule
     */
    public static isRuleSatisfied(colorRule: IQueryColorRule, resultCount: number): boolean {
        if (ColorRuleInterpreter.isRuleValidAndEnabled(colorRule)) {
            return ColorRuleInterpreter.evaluate(resultCount, colorRule.operator, colorRule.thresholdCount);
        }
        return false;
    }

    /**
     * supports simple evaluations of two numbers, using operators described in validQueryOperators array
     */
    public static evaluate(leftVal: number, operator: string, rightVal: number): boolean {
        let result = false;
        switch (operator) {
            case "<":
                result = leftVal < rightVal;
                break;
            case ">":
                result = leftVal > rightVal;
                break;
            case "<=":
                result = leftVal <= rightVal;
                break;
            case ">=":
                result = leftVal >= rightVal;
                break;
            case "<>":
                result = leftVal != rightVal;
                break;
            case "=":
                result = leftVal == rightVal;
                break;
            default:
                throw Error("An unsupported operator was selected.");
        }

        return result;
    }

    /**
     * Checks if a rule is correctly defined and enabled
     * @param {IQueryColorRule} colorRule - The rule to check
     * @returns true if the rule is enabled and valid
     */
    public static isRuleValidAndEnabled(colorRule: IQueryColorRule): boolean {
        let validRule = false;

        if (colorRule && colorRule.isEnabled) {
            const operatorIndex = validQueryColorOperators.indexOf(colorRule.operator);

            validRule = operatorIndex >= 0 && HexColorCodeValidator.isValid(colorRule.backgroundColor) && colorRule.thresholdCount != null;
        }

        return validRule;
    }

    /**
     * Evaluates an array of color rules against the query result count and returns
     * the background color for the widget to use based upon which rules are satisfied.
     * If no rules are satisfied then the fallback background color is used.
     * Rule precedence is that a later satisfied rule in the array wins over an earlier satisfied rule.
     * @param {IQueryColorRule[]} colorRules - The rules to evaluate
     * @param {number} resultCount - The number to check the rules against
     * @returns The hex color code to use for the background color or null if no rules are matched
     */
    public static getBackgroundColor(
        customDefaultBackgroundColor: string | null | undefined,
        colorRules: IQueryColorRule[],
        resultCount: number
    ): string {
        let backgroundColor = null;

        if (colorRules && resultCount != null) {
            colorRules.forEach((rule: IQueryColorRule) => {
                if (ColorRuleInterpreter.isRuleSatisfied(rule, resultCount)) {
                    backgroundColor = rule.backgroundColor;
                }
            });
        }

        if (backgroundColor == null) {
            backgroundColor = ColorRuleInterpreter.convertOldColor(
                customDefaultBackgroundColor ? customDefaultBackgroundColor : ColorRuleInterpreter.defaultBackgroundColor
            );
        }

        return backgroundColor;
    }

    /**
     * Convert the color string from previous queryScalar to match the new color picker
     * @param oldColor - The hex string of the color
     */
    public static convertOldColor(oldColor: string): string {
        let newColorString = oldColor;
        if (oldColor == "#339933") {
            newColorString = "#339947";
        } else if (oldColor == "#E51400") {
            newColorString = "#E60017";
        } else if (oldColor == "#009BCC") {
            newColorString == ColorRuleInterpreter.defaultBackgroundColor;
        }
        return newColorString;
    }
}
